<script setup lang="ts">
import type { ComponentExposed } from 'vue-component-type-helpers';
import type { DInput, DInputNumber } from '#components';
import type { PrimitiveType } from '#layers/data-model/public';
import { computed, toValue, useId, useTemplateRef } from 'vue';
import { isNil } from '#layers/base/public';
import { getDecimalConstraints, getInteger64InputProps, getNumberInputOptions, isPrimitiveTypeBoolean, isPrimitiveTypeDecimal, isPrimitiveTypeInteger64, isPrimitiveTypeJson, isPrimitiveTypeNumber, toDecimal } from '#layers/data-model/public';
import { useAutoWidthInput } from '#layers/ui/public';

interface Props {
	primitiveType: PrimitiveType;
	typeOptions?: Record<string, unknown>;
	disabled?: boolean;
	readonly?: boolean;
	/**
	 * Show a small (short) version of the input
	 */
	small?: boolean;
}

const props = defineProps<Props>();

const value = defineModel<string | number | boolean | Record<string, unknown> | null | undefined>();

const stringValue = computed({
	get() {
		const v = toValue(value);

		if (isNil(v)) {
			return;
		}

		if (isPrimitiveTypeJson(props.primitiveType)) {
			return JSON.stringify(v);
		}

		return String(v);
	},
	set(newValue: string | null | undefined) {
		if (props.disabled || props.readonly) return;

		if (isPrimitiveTypeJson(props.primitiveType)) {
			try {
				value.value = newValue ? JSON.parse(newValue) : undefined;
			}
			catch {
				return;
			}

			return;
		}

		if (newValue === null && value.value === null) return;

		if (isNil(newValue) || newValue === '') {
			value.value = undefined;
			return;
		}

		value.value = isPrimitiveTypeDecimal(props.primitiveType) ? toDecimal(newValue) : newValue;
	},
});

const numberValue = computed({
	get() {
		const v = toValue(value);

		if (isNil(v)) {
			return;
		}

		return Number(v);
	},
	set(newValue: number | undefined) {
		if (props.disabled || props.readonly) return;
		value.value = newValue;
	},
});

const inputRef = useTemplateRef<ComponentExposed<typeof DInput | typeof DInputNumber>>('input');
useAutoWidthInput(() => inputRef.value?.inputRef ?? null);

const inputType = computed(() => {
	if (isPrimitiveTypeDecimal(props.primitiveType)) {
		return 'decimal';
	}

	if (isPrimitiveTypeNumber(props.primitiveType)) {
		return 'number';
	}

	return 'text';
});

const inputId = useId();

const inputUi = computed(() => ({
	// The number inputs theme their root `h-fit`, which parks the input at the top of the filter
	// row instead of centering it. Stretching the root puts it back on the row's center line.
	root: 'h-full',
	base: ['max-w-40 px-2 focus-within:min-w-20', props.small ? 'min-w-5' : 'min-w-20'],
}));

// Always set on the branch that reads it: string-encoded numbers are rendered by the decimal input,
// so only narrower integer and float types reach `DInputNumber`. The optional access is there for
// the type, not for a case.
const numberOptions = computed(() => getNumberInputOptions(props.primitiveType));

const decimalConstraints = computed(() => getDecimalConstraints(props.primitiveType));

const integer64Props = computed(() => isPrimitiveTypeInteger64(props.primitiveType) ? getInteger64InputProps(props.primitiveType.name) : undefined);

const commonProps = computed(() => ({
	'id': inputId,
	'variant': 'none',
	'color': 'neutral',
	'size': 'xs',
	'disabled': props.disabled,
	'readonly': props.readonly,
	'aria-readonly': props.readonly,
	'ui': inputUi.value,
} as const));
</script>

<template>
	<DCheckbox
		v-if="isPrimitiveTypeBoolean(primitiveType)"
		:id="inputId"
		v-model="value"
		:disabled="disabled || readonly"
		:readonly
		class="h-full items-center px-1.5"
	/>
	<DInputDecimalNumber
		v-else-if="isPrimitiveTypeDecimal(primitiveType)"
		ref="input"
		v-bind="commonProps"
		v-model="stringValue"
		:scale="decimalConstraints.scale"
		:precision="decimalConstraints.precision"
	/>
	<DInputDecimalNumber
		v-else-if="isPrimitiveTypeInteger64(primitiveType)"
		ref="input"
		v-bind="{ ...commonProps, ...integer64Props }"
		v-model="stringValue"
	/>
	<DInputNumber
		v-else-if="isPrimitiveTypeNumber(primitiveType)"
		ref="input"
		v-bind="commonProps"
		v-model="numberValue"
		:format-options="numberOptions?.formatOptions"
		:step-snapping="numberOptions?.stepSnapping"
	/>
	<DInput
		v-else-if="inputType === 'text'"
		ref="input"
		v-bind="commonProps"
		v-model="stringValue"
	/>
</template>
